// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { DataState, TimePeriod, DashboardPanel } from "types";
import { UserRole } from "graphql/api";
import { ICloudParameter, IEnergyMeter, ITariffCircuit, IVirtAssign } from "parameter";
import { ActionType, IAction, IActionTrend, raiseAction } from "actions";
import { isSet, roundToDigits } from "utils";
import {
  formatEnergyValue,
  formatPowerValue,
  getColor,
  getGraphBoundaryDates,
  getPanelSelection,
} from "guiutils";
import { IState } from "states/states";
import { IPanelSelection } from "states/cachedstates";
import { ITrendData, ITrendDataRow, ITrendDataTimestamp } from "states/trendingstates";
import TariffCircuitListModel from "model/tariffcircuitlist";
import { IDropdownSelectOption } from "./DropdownSelect";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp//FormattedTime";
import ModalTrendData from "view/modal/ModalTrendData";
import { ReactComponent as IconEdit } from "static/iconEdit.svg";

// uses webpack definitions
declare const IS_CLOUD: boolean;

const DIGITS_TO_SHOW = 3;

interface ITrendDatatableProps {
  siteId: number | undefined;
  dataState: DataState;
  trendLoadingProgress: number;
  trendMember: "energy" | "power" | "costs";
  startDate: number | undefined;
  endDate: number | undefined;
  trendData: ITrendData;
  timePeriod: TimePeriod;
  tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  virtualTariffCircuits: IVirtAssign[];
  selection: IPanelSelection | undefined;
  // deviceTimestamp: number | undefined;
  timezoneOffset: number | undefined;
  canModifyData: boolean;
}

const mapStateToProps = (state: IState) => {
  const tariffCircuits = state.parameter?.tariffline ?? [];
  const { index, selection } = getPanelSelection(state, DashboardPanel.ENERGY);

  const loginRole =
    state.site.userList?.find((curUser) => curUser.secId === state.site.user?.secId)?.role ??
    UserRole.viewer;

  const props: ITrendDatatableProps = {
    siteId: state.cached.selectedSite,
    dataState: state.scheduler.trendLoadingState,
    trendLoadingProgress: state.scheduler.trendLoadingProgress,
    trendMember: state.gui.trendMember,
    startDate: state.gui.trendPeriod.startDate,
    endDate: state.gui.trendPeriod.endDate,
    trendData: state.trending.trendData,
    timePeriod: state.gui.trendPeriod.timePeriod,
    tariffCircuits,
    energyMeters: state.parameter?.energymeter ?? [],
    virtualTariffCircuits: (state.parameter as ICloudParameter)?.virtualSite?.tariffline ?? [],
    selection,
    // we only need the device timestamp if graph start date is not set
    // @@@Marc: do not use the deviceTimestamp for now, since this timestamp is changeing
    // with the live-data every 5 Seconds, which would cause a refresh of the Graphs
    // deviceTimestamp:
    //   state.gui.trendPeriod.startDate === undefined ? state.data.timestamp : undefined,
    timezoneOffset: state.site.tzOffset,
    canModifyData:
      IS_CLOUD &&
      loginRole !== UserRole.viewer &&
      state.gui.trendPeriod.timePeriod === TimePeriod.DAY,
  };
  return props;
};

interface ITrendDatatableDispatch {
  onEdit: (timestamp: number, tlId: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendDatatableDispatch = {
    onEdit: (timestamp: number, tlIndex: number) => {
      const payload: IActionTrend = {
        timestamp,
        tlIndex,
      };
      dispatch(raiseAction(ActionType.ACT_EDIT_TRENDDATA, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_edit: {
    id: "change value",
  },
});

class TrendDatatableComponent extends React.Component<
  ITrendDatatableProps & ITrendDatatableDispatch & WrappedComponentProps
> {
  private startTime?: number;
  private endTime?: number;
  private selectedTariffCircuits: IDropdownSelectOption[] = [];

  private getTimestamps() {
    const graphDates = getGraphBoundaryDates(
      this.props.startDate,
      this.props.endDate,
      this.props.timezoneOffset,
      this.props.timePeriod,
    );
    this.startTime = graphDates.startDate;
    this.endTime = graphDates.endDate;
    const allTimestamps = Object.keys(this.props.trendData)
      .map((item) => parseInt(item))
      .filter((curItem) => curItem >= this.startTime! && curItem < this.endTime!);
    return allTimestamps;
  }

  private initData() {
    const showAllChildren = this.props.selection?.hierarchyId === undefined;
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    const tariffCircuitIndexes = tcModel.getTariffCircuits(
      this.props.selection?.energySource,
      false,
      this.props.selection?.hierarchyId,
      this.props.selection?.hierarchyId === undefined,
      this.props.selection?.category,
      showAllChildren,
    );
    this.selectedTariffCircuits = tcModel
      .makeOptionsArray(
        tariffCircuitIndexes,
        this.props.selection?.tariffCircuitIds,
        showAllChildren,
      )
      .filter((curItem) => curItem.visible);
  }

  public render() {
    if (this.props.dataState === DataState.SAVE_REQ) {
      return (
        <div className="loading-spinner-box">
          <div id="loading-spinner"></div>
          <div id="loading-text">
            {this.props.trendLoadingProgress > 0 ? (
              <>{Math.round(100 * this.props.trendLoadingProgress)} %</>
            ) : (
              <></>
            )}
          </div>
        </div>
      );
    }

    if (this.props.dataState === DataState.SAVE_FAIL) {
      return (
        <div className="datatable" style={{ minHeight: 150 }}>
          <FormattedMessage id="Failed to load data." />
        </div>
      );
    }

    this.initData();

    const rows: any[] = [];
    const timestamps = this.getTimestamps();
    const sortedTariffCircuits = this.selectedTariffCircuits.sort((a, b) =>
      this.props.tariffCircuits[a.index].name.localeCompare(
        this.props.tariffCircuits[b.index].name,
      ),
    );
    // loop through timestamps and sorted tariff circuits
    timestamps.forEach((curTimestamp) => {
      rows.push(this.renderTimestamp(curTimestamp, sortedTariffCircuits));
    });

    return (
      <>
        <table className="datatable fullwidthwhite">
          <thead>
            <tr>
              <th>
                <FormattedMessage id="Timestamp" />
              </th>
              {this.selectedTariffCircuits.length > 0 && (
                <th>
                  <FormattedMessage id="Tariffcircuit" />
                </th>
              )}
              {this.props.timePeriod === TimePeriod.DAY && (
                <th className="right">
                  <FormattedMessage id="Period" />
                </th>
              )}
              {this.props.trendMember === "energy" && (
                <th className="right">
                  <FormattedMessage id="Energy" />
                </th>
              )}
              {this.props.trendMember === "costs" && (
                <th className="right">
                  <FormattedMessage id="Costs Energy" />
                </th>
              )}
              {this.props.trendMember === "power" && (
                <>
                  <th className="right">
                    <FormattedMessage id="Power" />
                  </th>
                  <th className="right">
                    <FormattedMessage id="Target Value" />
                  </th>
                </>
              )}
              {this.props.canModifyData && <th />}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
          <tfoot>{this.renderSumRow(sortedTariffCircuits)}</tfoot>
        </table>
        <ModalTrendData />
      </>
    );
  }

  private renderTimestamp(timestamp: number, sortedTariffCircuits: IDropdownSelectOption[]) {
    const curTimeData = this.props.trendData[timestamp];
    let isFirstRow = true;
    const rowCount = sortedTariffCircuits.filter((curItem) =>
      curTimeData[curItem.index] ? true : false,
    ).length;
    const tlItems = sortedTariffCircuits.map((curItem) => {
      const curDataRow = curTimeData[curItem.index];
      if (curDataRow) {
        const tariffCircuit = this.props.tariffCircuits[curItem.index] as
          | ITariffCircuit
          | undefined;
        const color = rowCount > 1 ? getColor(curItem.index) : "black";
        const isVirtualTariffCircuit = this.props.virtualTariffCircuits.some(
          (curVirtTc) =>
            curVirtTc.destIndex === curItem.index && curVirtTc.sourceSite !== this.props.siteId,
        );

        const row = (
          <tr key={`${timestamp}${curItem.index}`}>
            {isFirstRow && (
              <td rowSpan={rowCount}>
                <FormattedDate
                  valueMs={timestamp * 1000}
                  noDay={this.props.timePeriod === TimePeriod.YEAR}
                />
                {[TimePeriod.DAY, TimePeriod.WEEK].includes(this.props.timePeriod) && (
                  <>
                    {" "}
                    <FormattedTime valueMs={timestamp * 1000} />
                  </>
                )}
              </td>
            )}
            {this.selectedTariffCircuits.length > 0 && (
              <td style={{ color }}>{tariffCircuit?.name}</td>
            )}
            {this.props.timePeriod === TimePeriod.DAY && (
              <td className="right nowrap">
                {roundToDigits(curDataRow.regperiod, DIGITS_TO_SHOW)} s
              </td>
            )}
            {this.props.trendMember === "energy" && (
              <td className="right nowrap">
                {formatEnergyValue(curDataRow.energy, tariffCircuit?.unit)}
              </td>
            )}
            {this.props.trendMember === "costs" && (
              <td className="right nowrap">
                {isSet(curDataRow.costs)
                  ? `${roundToDigits(curDataRow.costs, DIGITS_TO_SHOW)} EUR`
                  : "-"}
              </td>
            )}
            {this.props.trendMember === "power" && (
              <>
                <td className="right nowrap">
                  {formatPowerValue(curDataRow.power, tariffCircuit?.unit)}
                </td>
                <td className="right nowrap">
                  {formatPowerValue(curDataRow.targetpower, tariffCircuit?.unit)}
                </td>
              </>
            )}
            {this.props.canModifyData &&
              (isVirtualTariffCircuit ? (
                <td></td>
              ) : (
                <td
                  onClick={() => {
                    this.props.onEdit(timestamp, curItem.index);
                  }}
                  className="edit-col img-link"
                  title={this.props.intl.formatMessage(messages.msg_edit)}
                >
                  <IconEdit />
                </td>
              ))}
          </tr>
        );
        isFirstRow = false;
        return row;
      } else {
        // this may happen on inactive tariff lines
        //console.error("data row not found", curTimeData, tlId);
        return <React.Fragment key={`${timestamp}${curItem.index}`}></React.Fragment>;
      }
    });
    return <React.Fragment key={timestamp}>{tlItems}</React.Fragment>;
  }

  private renderSumRow(sortedTariffs: IDropdownSelectOption[]) {
    // loop through all trend data to find all tariff circuit ids
    const tlIds: number[] = [];
    (Object.values(this.props.trendData) as ITrendDataTimestamp[]).forEach((curTrend) => {
      curTrend.forEach((curItem, curIndex) => {
        if (curItem && !tlIds.includes(curIndex)) {
          tlIds.push(curIndex);
        }
      });
    });
    let isFirstRow = true;
    const tlItems = sortedTariffs.map((curItem, index) => {
      const tlId = curItem.index;
      if (!tlIds.includes(tlId)) {
        return <></>;
      }
      const isRegulation = (Object.values(this.props.trendData) as ITrendDataTimestamp[]).some(
        (curTrend) => isSet(curTrend[tlId]?.targetpower),
      );
      const tariffCircuit = this.props.tariffCircuits[tlId] as ITariffCircuit | undefined;
      const color = sortedTariffs.length > 1 ? getColor(curItem.index) : "black";
      const row = (
        <tr key={`sum_${tlId}`}>
          {isFirstRow && (
            <th rowSpan={tlIds.length}>
              <FormattedMessage id="Sum / Max" />
            </th>
          )}
          {this.selectedTariffCircuits.length > 0 && (
            <td style={{ color }}>{tariffCircuit?.name}</td>
          )}
          {this.props.timePeriod === TimePeriod.DAY && (
            <td className="right nowrap">
              {`${roundToDigits(this.getTrendSum(tlId, "regperiod"), DIGITS_TO_SHOW)} s`}
            </td>
          )}
          {this.props.trendMember === "energy" && (
            <td className="right nowrap">
              {formatEnergyValue(this.getTrendSum(tlId, "energy"), tariffCircuit?.unit)}
            </td>
          )}
          {this.props.trendMember === "costs" && (
            <td className="right nowrap">
              {`${roundToDigits(this.getTrendSum(tlId, "costs"), DIGITS_TO_SHOW)} EUR`}
            </td>
          )}
          {this.props.trendMember === "power" && (
            <>
              <td className="right nowrap">
                {formatPowerValue(this.getTrendMax(tlId, "power"), tariffCircuit?.unit)}
              </td>
              <td className="right nowrap">
                {isRegulation
                  ? formatPowerValue(this.getTrendMax(tlId, "targetpower"), tariffCircuit?.unit)
                  : "-"}
              </td>
            </>
          )}
          {this.props.canModifyData && <td></td>}
        </tr>
      );
      isFirstRow = false;
      return row;
    });
    return <React.Fragment>{tlItems}</React.Fragment>;
  }

  private getTrendSum(tlId: number, trendMember: keyof ITrendDataRow) {
    return (Object.values(this.props.trendData) as ITrendDataTimestamp[])
      .map((curDataRow) => {
        return curDataRow[tlId];
      })
      .reduce((prev, curDataRow) => {
        return curDataRow ? prev + curDataRow[trendMember]! : prev;
      }, 0);
  }
  private getTrendMax(tlId: number, trendMember: keyof ITrendDataRow) {
    return (Object.values(this.props.trendData) as ITrendDataTimestamp[])
      .map((curDataRow) => {
        return curDataRow[tlId];
      })
      .reduce((prev, curDataRow) => {
        return curDataRow ? Math.max(prev, curDataRow[trendMember]!) : prev;
      }, 0);
  }
}

const TrendDatatable = injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(TrendDatatableComponent),
);

export default TrendDatatable;
